import { useState } from "react";
import { MessageCircle, Loader2, Send, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await api.submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
      });
      toast.success("Message received.", {
        description: "We'll be in touch soon — thank you for reaching out.",
      });
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 mb-5">
              <MessageCircle size={14} className="text-[#692128]" />
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B2635]">
                Reach Out
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2A29] leading-[1.05] tracking-tight">
              We'd love
              <br />
              to hear from you.
            </h2>
            <p className="mt-5 text-[#5C5856] text-lg leading-relaxed">
              Whether you need support, want to volunteer, or hope to partner — start the
              conversation. We read every note.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#692128]/10 text-[#692128] flex items-center justify-center shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#8B2635] mb-1">
                    Visit Us
                  </div>
                  <address className="not-italic text-[#2C2A29] leading-relaxed">
                    1820 Highway 20 SE, Suite 114
                    <br />
                    Conyers, GA 30013
                  </address>
                  <p className="text-sm text-[#5C5856] mt-1">Serving families nationwide</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#692128]/10 text-[#692128] flex items-center justify-center shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#8B2635] mb-1">
                    Write Us
                  </div>
                  <p className="text-[#2C2A29]">
                    Use the form — we'll respond personally within a few business days.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white border border-[#E2DCD0] p-8 sm:p-10 shadow-sm">
              {done ? (
                <div data-testid="contact-success" className="text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-[#2A4747] text-[#F4F1EB] flex items-center justify-center mx-auto mb-5">
                    <Send size={20} />
                  </div>
                  <h3 className="font-serif text-3xl text-[#2C2A29]">Message received.</h3>
                  <p className="mt-3 text-[#5C5856]">
                    Thank you for reaching out. We'll respond personally — usually within a few business days.
                  </p>
                  <button
                    onClick={() => {
                      setDone(false);
                      setForm({ name: "", email: "", subject: "", message: "" });
                    }}
                    data-testid="contact-send-another"
                    className="mt-6 inline-flex items-center gap-2 text-[#692128] hover:text-[#8B2635] text-sm font-semibold tracking-wide underline-offset-4 hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs tracking-[0.18em] uppercase font-bold text-[#8B2635] mb-2">
                        Your name *
                      </label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        data-testid="contact-name"
                        className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#FAF8F5] focus:bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.18em] uppercase font-bold text-[#8B2635] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        data-testid="contact-email"
                        className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#FAF8F5] focus:bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.18em] uppercase font-bold text-[#8B2635] mb-2">
                      Subject (optional)
                    </label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={update("subject")}
                      placeholder="Volunteering, partnership, support…"
                      data-testid="contact-subject"
                      className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#FAF8F5] focus:bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.18em] uppercase font-bold text-[#8B2635] mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us a little about what brought you here…"
                      data-testid="contact-message"
                      className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-[#FAF8F5] focus:bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29] resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group inline-flex items-center gap-2 bg-[#692128] text-[#F4F1EB] hover:bg-[#8B2635] disabled:opacity-70 rounded-full px-8 py-3.5 text-sm font-semibold tracking-wide transition-all"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        <Send size={16} /> Send message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
